import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check, Code2, Loader2, Mail, MousePointerClick, PlugZap, ShoppingBag, TrendingUp } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageContainer } from '../../../common/components/PageContainer';
import { InfluenceReport, PixelSnippet, useLeadDeskApi } from '../../../common/hooks/use-lead-desk-api.hook';
import { KpiCard, PillTabs, RelativeTime, SectionCard, StatusBadge, formatCurrency, formatDate, formatNumber } from '../../../components/design-system/admin';
import { TrendChart } from '../../../components/design-system/analytics';
import { CopyField } from '../../../components/design-system/common';
import { INFLUENCE_PERIODS, InfluencePeriod, influenceRange, roiLabel } from '../../../modules/vendor/lead-desk/lead-desk-view';

const percent = (v: number | null | undefined, digits = 1) =>
    v === null || v === undefined ? '—' : `${formatNumber(v * 100, digits)} %`;

/** A setup step: what it unlocks, whether it is done, and the way to do it. */
function SetupStep({ icon, title, body, done, status, action, children, testId }: {
    icon: React.ReactNode; title: string; body: string; done: boolean; status?: React.ReactNode; action?: React.ReactNode; children?: React.ReactNode; testId?: string;
}) {
    return (
        <div className="flex flex-col gap-3 rounded-xl border p-4" data-testid={testId}>
            <div className="flex items-start gap-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border text-muted-foreground [&_svg]:size-4">{icon}</span>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{title}</p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{body}</p>
                </div>
            </div>
            {status && <StatusBadge tone={done ? 'success' : 'neutral'} className={cn(!done && 'text-muted-foreground')}>{status}</StatusBadge>}
            {children}
            {action && <div>{action}</div>}
        </div>
    );
}

/**
 * Sales influenced — the number that renews contracts: revenue from orders
 * placed by people who used Ardealy before they bought, against what the
 * vendor pays for it; how it is counted, in plain words; and what to set up
 * so every sale can be counted.
 */
export const SalesInfluencePage = () => {
    const { t, i18n } = useTranslation();
    const api = useLeadDeskApi();
    const [period, setPeriod] = useState<InfluencePeriod>('6m');
    const [data, setData] = useState<InfluenceReport | null>(null);
    const [pixel, setPixel] = useState<PixelSnippet | null>(null);
    const [showPixel, setShowPixel] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            setData(await api.influence(influenceRange(period)));
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [api, period]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        api.pixel().then(setPixel).catch(() => setPixel(null));
    }, [api]);

    const currency = data?.currency ?? 'EUR';
    const money = (v: number | null | undefined) => formatCurrency(v ?? null, currency, 0);
    const monthLabel = (key: string) => {
        try {
            return new Intl.DateTimeFormat(i18n.language, { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(new Date(`${key}-01T00:00:00Z`));
        } catch {
            return key;
        }
    };
    const trend = useMemo(() => (data?.byMonth ?? []).map((m) => ({ label: m.month, value: m.influencedRevenue })), [data]);
    const roi = roiLabel(data?.roi);
    const setup = data?.setup;

    return (
        <PageContainer breadcrumbs={[{ label: t('R2R.WORKSPACE.SALES') }, { label: t('R2R.NAV.INFLUENCE') }]}>
            <div className="mx-auto flex w-full min-w-0 max-w-[1100px] flex-col gap-6" data-testid="sales-influence">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div className="min-w-0">
                        <h1 className="font-heading text-[22px] font-medium tracking-tight">{t('LEAD_DESK.INFLUENCE.TITLE')}</h1>
                        <p className="mt-1 text-sm text-muted-foreground">{t('LEAD_DESK.INFLUENCE.SUBTITLE')}</p>
                    </div>
                    <PillTabs<InfluencePeriod>
                        value={period}
                        onChange={setPeriod}
                        ariaLabel={t('LEAD_DESK.INFLUENCE.TITLE')}
                        options={INFLUENCE_PERIODS.map((p) => ({ value: p, label: t(`LEAD_DESK.INFLUENCE.PERIOD.${p.toUpperCase()}`) }))}
                    />
                </div>

                {loading && !data && (
                    <div className="flex justify-center py-16"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>
                )}
                {error && (
                    <Card>
                        <CardContent className="flex flex-wrap items-center justify-between gap-3">
                            <p className="text-sm text-muted-foreground">{t('LEAD_DESK.LOAD_FAILED')}</p>
                            <Button variant="outline" size="sm" onClick={load}>{t('LEAD_DESK.RETRY')}</Button>
                        </CardContent>
                    </Card>
                )}

                {data && (
                    <>
                        {/* The headline: what Ardealy influenced, and how many times the plan that is. */}
                        <Card className="gap-0 py-0" data-testid="influence-headline">
                            <CardContent className="flex flex-col gap-2 p-6">
                                {data.influencedOrders > 0 ? (
                                    <>
                                        <p className="font-heading text-[28px] leading-tight font-medium tracking-tight">
                                            {t('LEAD_DESK.INFLUENCE.HEADLINE', { amount: money(data.influencedRevenue) })}
                                            {roi && (
                                                <span className="text-muted-foreground">
                                                    {' — '}
                                                    {t(data.roiApprox ? 'LEAD_DESK.INFLUENCE.HEADLINE_ROI_APPROX' : 'LEAD_DESK.INFLUENCE.HEADLINE_ROI', { roi })}
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            {t('LEAD_DESK.INFLUENCE.HEADLINE_SHARE', { share: percent(data.share), orders: formatNumber(data.influencedOrders) })}
                                            {' · '}
                                            {formatDate(data.from)} – {formatDate(data.to)}
                                            {loading && <Loader2 className="ms-2 inline size-3 animate-spin" />}
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-[15px]">{setup?.shopConnected ? t('LEAD_DESK.INFLUENCE.NONE_YET') : t('LEAD_DESK.INFLUENCE.NOT_CONNECTED')}</p>
                                )}
                                {!setup?.shopConnected && <p className="max-w-[640px] text-sm text-muted-foreground">{t('LEAD_DESK.INFLUENCE.NOT_CONNECTED_BODY')}</p>}
                            </CardContent>
                        </Card>

                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <KpiCard testId="influence-kpi-revenue" icon={<TrendingUp />} label={t('LEAD_DESK.INFLUENCE.KPI.REVENUE')} value={money(data.influencedRevenue)} hint={t('LEAD_DESK.INFLUENCE.KPI.REVENUE_HINT', { total: money(data.totalRevenue) })} />
                            <KpiCard testId="influence-kpi-orders" icon={<ShoppingBag />} label={t('LEAD_DESK.INFLUENCE.KPI.ORDERS')} value={formatNumber(data.influencedOrders)} hint={t('LEAD_DESK.INFLUENCE.KPI.ORDERS_HINT', { total: formatNumber(data.totalOrders) })} />
                            <KpiCard testId="influence-kpi-share" label={t('LEAD_DESK.INFLUENCE.KPI.SHARE')} value={percent(data.share)} hint={t('LEAD_DESK.INFLUENCE.KPI.SHARE_HINT')} />
                            <KpiCard
                                testId="influence-kpi-roi"
                                label={t('LEAD_DESK.INFLUENCE.KPI.ROI')}
                                value={roi ?? '—'}
                                hint={data.planMonthly && data.planCost != null
                                    ? `${t('LEAD_DESK.INFLUENCE.KPI.ROI_HINT', { cost: money(data.planCost), months: formatNumber(data.months, 1) })}${data.roiApprox ? ` · ${t('LEAD_DESK.INFLUENCE.KPI.ROI_APPROX')}` : ''}`
                                    : t('LEAD_DESK.INFLUENCE.KPI.ROI_NONE')}
                            />
                        </div>

                        <SectionCard title={t('LEAD_DESK.INFLUENCE.BY_MONTH')} description={t('LEAD_DESK.INFLUENCE.BY_MONTH_DESC')} testId="influence-by-month">
                            <TrendChart data={trend} valueLabel={t('LEAD_DESK.INFLUENCE.INFLUENCED')} formatValue={(v) => formatCurrency(v, currency, 0)} formatLabel={monthLabel} height={200} />
                            <div className="mt-4 border-t">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>{t('LEAD_DESK.INFLUENCE.MONTH')}</TableHead>
                                            <TableHead className="text-end">{t('LEAD_DESK.INFLUENCE.INFLUENCED')}</TableHead>
                                            <TableHead className="text-end">{t('LEAD_DESK.INFLUENCE.TOTAL')}</TableHead>
                                            <TableHead className="text-end">{t('LEAD_DESK.INFLUENCE.SHARE')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {[...data.byMonth].reverse().map((m) => (
                                            <TableRow key={m.month}>
                                                <TableCell>{monthLabel(m.month)}</TableCell>
                                                <TableCell className="text-end tabular-nums">{money(m.influencedRevenue)} <span className="text-xs text-muted-foreground">· {formatNumber(m.influencedOrders)}</span></TableCell>
                                                <TableCell className="text-end tabular-nums text-muted-foreground">{money(m.totalRevenue)}</TableCell>
                                                <TableCell className="text-end tabular-nums">{m.totalRevenue > 0 ? percent(m.influencedRevenue / m.totalRevenue) : '—'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </SectionCard>

                        {/* How it is counted, in plain words. */}
                        <SectionCard title={t('LEAD_DESK.INFLUENCE.METHOD')} testId="influence-method">
                            <div className="flex flex-col gap-3 text-sm leading-relaxed">
                                <p>{t('LEAD_DESK.INFLUENCE.METHOD_INTRO', { days: data.method.windowDays })}</p>
                                <p className="flex gap-2"><MousePointerClick className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />{t('LEAD_DESK.INFLUENCE.METHOD_PIXEL', { n: formatNumber(data.method.viaPixel) })}</p>
                                <p className="flex gap-2"><Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />{t('LEAD_DESK.INFLUENCE.METHOD_EMAIL', { n: formatNumber(data.method.viaEmail) })}</p>
                                <p className="text-muted-foreground">{t('LEAD_DESK.INFLUENCE.METHOD_CANCELLED')}</p>
                                {data.method.unchecked > 0 && <p className="text-muted-foreground">{t('LEAD_DESK.INFLUENCE.METHOD_UNCHECKED', { n: formatNumber(data.method.unchecked) })}</p>}
                                {data.unmatchedCheckouts.count > 0 && (
                                    <p className="text-muted-foreground" data-testid="influence-unmatched">
                                        {t('LEAD_DESK.INFLUENCE.UNMATCHED', {
                                            n: formatNumber(data.unmatchedCheckouts.count),
                                            total: data.unmatchedCheckouts.reportedTotal != null ? t('LEAD_DESK.INFLUENCE.UNMATCHED_TOTAL', { amount: money(data.unmatchedCheckouts.reportedTotal) }) : '',
                                        })}
                                    </p>
                                )}
                            </div>
                        </SectionCard>

                        {/* What to set up so every sale can be counted. */}
                        <SectionCard title={t('LEAD_DESK.INFLUENCE.SETUP')} testId="influence-setup">
                            <div className="grid gap-4 lg:grid-cols-3">
                                <SetupStep
                                    testId="setup-shop"
                                    icon={<PlugZap />}
                                    title={t('LEAD_DESK.INFLUENCE.SETUP_SHOP')}
                                    body={t('LEAD_DESK.INFLUENCE.SETUP_SHOP_BODY')}
                                    done={!!setup?.shopConnected && !!setup?.customersGrant}
                                    status={setup?.shopConnected
                                        ? setup.customersGrant
                                            ? t('LEAD_DESK.INFLUENCE.SETUP_SHOP_DONE', { shop: setup.shopDomain ?? setup.platform })
                                            : t('LEAD_DESK.INFLUENCE.SETUP_SHOP_NO_GRANT', { shop: setup.shopDomain ?? setup.platform })
                                        : undefined}
                                    action={(
                                        <Button asChild variant="outline" size="sm">
                                            <Link to="/business/sales/shop">{t('LEAD_DESK.INFLUENCE.SETUP_SHOP_ACTION')}<ArrowUpRight className="rtl:-scale-x-100" /></Link>
                                        </Button>
                                    )}
                                />
                                <SetupStep
                                    testId="setup-pixel"
                                    icon={<Code2 />}
                                    title={t('LEAD_DESK.INFLUENCE.SETUP_PIXEL')}
                                    body={t('LEAD_DESK.INFLUENCE.SETUP_PIXEL_BODY')}
                                    done={(setup?.checkoutsReported ?? 0) > 0}
                                    status={(setup?.checkoutsReported ?? 0) > 0
                                        ? <>{t('LEAD_DESK.INFLUENCE.SETUP_PIXEL_SEEN', { n: formatNumber(setup!.checkoutsReported) })}<RelativeTime value={setup!.lastCheckoutAt} className="ms-1" /></>
                                        : t('LEAD_DESK.INFLUENCE.SETUP_PIXEL_NOT_SEEN')}
                                    action={pixel && (
                                        <Button variant="outline" size="sm" onClick={() => setShowPixel((v) => !v)} data-testid="setup-pixel-toggle">
                                            {showPixel ? t('LEAD_DESK.INFLUENCE.SETUP_PIXEL_HIDE') : t('LEAD_DESK.INFLUENCE.SETUP_PIXEL_SHOW')}
                                        </Button>
                                    )}
                                >
                                    {showPixel && pixel && <CopyField value={pixel.shopify} multiline className="font-mono text-[11px]" testId="setup-pixel-code" />}
                                </SetupStep>
                                <SetupStep
                                    testId="setup-sdk"
                                    icon={<Check />}
                                    title={t('LEAD_DESK.INFLUENCE.SETUP_SDK')}
                                    body={t('LEAD_DESK.INFLUENCE.SETUP_SDK_BODY')}
                                    done={(setup?.sdkVisitors ?? 0) > 0}
                                    status={(setup?.sdkVisitors ?? 0) > 0 ? t('LEAD_DESK.INFLUENCE.SETUP_SDK_SEEN', { n: formatNumber(setup!.sdkVisitors) }) : undefined}
                                />
                            </div>
                        </SectionCard>

                        <SectionCard title={t('LEAD_DESK.INFLUENCE.ORDERS')} description={t('LEAD_DESK.INFLUENCE.ORDERS_DESC')} flush testId="influence-orders">
                            {data.orders.length ? (
                                <div className="border-t">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="ps-4 sm:ps-6">{t('LEAD_DESK.INFLUENCE.ORDER')}</TableHead>
                                                <TableHead>{t('LEAD_DESK.INFLUENCE.DATE')}</TableHead>
                                                <TableHead className="text-end">{t('LEAD_DESK.INFLUENCE.AMOUNT')}</TableHead>
                                                <TableHead>{t('LEAD_DESK.INFLUENCE.VIA')}</TableHead>
                                                <TableHead>{t('LEAD_DESK.INFLUENCE.LEAD')}</TableHead>
                                                <TableHead className="pe-4 sm:pe-6">{t('LEAD_DESK.INFLUENCE.FIRST_VISIT')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {data.orders.map((o) => (
                                                <TableRow key={o.id}>
                                                    <TableCell className="ps-4 sm:ps-6">
                                                        <p className="text-sm">{o.orderNumber ?? '—'}</p>
                                                        <p className="max-w-[260px] truncate text-xs text-muted-foreground">{o.products.map((p) => p.title).filter(Boolean).join(', ')}</p>
                                                    </TableCell>
                                                    <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(o.placedAt)}</TableCell>
                                                    <TableCell className="text-end tabular-nums">{formatCurrency(o.total, o.currency ?? currency, 0)}</TableCell>
                                                    <TableCell>
                                                        <StatusBadge tone="info">{o.via === 'pixel' ? t('LEAD_DESK.INFLUENCE.VIA_PIXEL') : t('LEAD_DESK.INFLUENCE.VIA_EMAIL')}</StatusBadge>
                                                    </TableCell>
                                                    <TableCell className="max-w-[220px] truncate">
                                                        {o.leadId ? <Link to={`/sales/leads/${o.leadId}`} className="text-sm hover:underline">{o.leadEmail ?? t('LEAD_DESK.OPEN_FILE')}</Link> : <span className="text-muted-foreground">—</span>}
                                                    </TableCell>
                                                    <TableCell className="pe-4 whitespace-nowrap text-muted-foreground sm:pe-6">
                                                        {o.daysToOrder != null ? t('LEAD_DESK.INFLUENCE.DAYS_BEFORE', { n: o.daysToOrder }) : '—'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : (
                                <p className="px-6 pb-6 text-sm text-muted-foreground">{t('LEAD_DESK.INFLUENCE.NO_ORDERS')}</p>
                            )}
                        </SectionCard>
                    </>
                )}
            </div>
        </PageContainer>
    );
};

export default SalesInfluencePage;
